import { useEffect, useState } from 'react';
import { ToolsDamInformation } from '../../components/custom';
import { LayoutBase } from '../../layouts';
import { IMantenance, MaintenancesServices } from '../../services/api/maintenances/MaintenancesServices';
import { useParams } from 'react-router-dom';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { CustomAlert, Loadin } from '../../components/shared';
import { AddComment, Delete, Download } from '@mui/icons-material';
import NewChat from '../../components/custom/maintenance/NewChat';
import { ChatServices } from '../../services/api/chat/chatServices';
import { formatDate } from '../../services/helpers/helpers';
import { useAuthContext } from '../../context';

type IChat = {
    arquivo: any,
    comentario: string,
    data: string,
    id_planejamento: string,
    id_responsavel: string,
    responsavel: string,
    uuid: string,
}

export default function MaintenanceChat() {

    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const { userData } = useAuthContext();
    const permissoes = userData.permissoes;
    const { maintenance } = useParams();
    const [dataManutencao, setDataManutencao] = useState<IMantenance | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [dataChat, setDataChat] = useState<IChat[]>([]);
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    function onClose() {
        setOpen(false);
        fetchDataChat(maintenance || '');
    }

    useEffect(() => {
        if(maintenance){
            fetchDataPlanejamento(maintenance);
            fetchDataChat(maintenance);
        }
    }, []);

    async function fetchDataPlanejamento(id: string) {
        MaintenancesServices.get(id).then((response) => {
            if (response instanceof Error) {
                console.log('Erro ao buscar dados', response);
            } else {
                setDataManutencao(response);
            }
        }).catch((error) => {
            setAlertInfo({ message: `${error || 'Verifique sua conexão.'}`, open: true, severity: 'error' }); 
        });
    }

    async function fetchDataChat(id: string) {
        ChatServices.list(id).then((response) => {
            if (response instanceof Error) {
                setAlertInfo({ message: `Erro inesperado ao buscar dados! ${response.message}`, open: true, severity: 'error' });
            } else {
                setDataChat(response);
            }
        }).catch((error) => {
            setAlertInfo({ message: `${error || 'Verifique sua conexão.'}`, open: true, severity: 'error' }); 
        });
    }

    function base64ToBlob(base64: string, mimeType: string): Blob {
        const byteCharacters = atob(base64);

        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }

    const handleDownload = (fileBase64: string) => {
        const blob = base64ToBlob(fileBase64, 'application/pdf');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Download.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    async function onDelete(uuid: string) {
        ChatServices.delet(uuid).then((response) => {
            if (response instanceof Error){
                console.error(`Erro ao deletar registro. Erro: ${response.message}`);
                setAlertInfo({ message: `Erro inesperado ao deletar! ${response.message || 'Verifique sua conexão.'}`, open: true, severity: 'error' });
            }else{
                fetchDataChat(maintenance || '');
                setAlertInfo({ message: 'Comentário deletado!', open: true, severity: 'success' });
            }
        });
    }

    return (
        <LayoutBase
            title='Chat de Manutenção'
            tollBar={<ToolsDamInformation/>}
        >
            {dataManutencao === null ? (
                <Box>
                    <Loadin />
                </Box>
            ):
            <Box>
                <Box sx={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <Typography variant='h4'>{dataManutencao.nome}</Typography>
                    {permissoes.includes('add') &&
                        <Button variant='contained' startIcon={<AddComment />} onClick={() => setOpen(true)}>{smDown ? 'Novo' : 'Novo Comentário'}</Button>
                    }
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2}}>
                    {dataChat.length === 0 ?
                        <Typography variant='h6' textAlign={'center'}>Nenhum comentário encontrado.</Typography> :
                        dataChat.map((chat) => (
                            <Box sx={{borderRadius: '25px', border: '1px solid', padding: 2}} key={chat.uuid}>
                                <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 1}}>
                                    <Typography>{chat.responsavel}</Typography>
                                    <Typography>Data: {formatDate(chat.data)}</Typography>
                                </Box>
                                <Typography>{chat.comentario}</Typography>
                                <Box sx={{marginTop: 1, display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-between'}}>
                                    {chat.arquivo && (
                                        <Button variant='outlined' startIcon={<Download/>} onClick={() => handleDownload(chat.arquivo)}>Download</Button>
                                    )}
                                    {chat.id_responsavel === userData.uuid && (
                                        <Button variant='outlined' startIcon={<Delete />} onClick={() => onDelete(chat.uuid)}>Excluir</Button>
                                    )}
                                </Box>
                            </Box>
                    ))}
                </Box>
            </Box>
            }
            <NewChat
                close={onClose}
                open={open}
                uuidManutencao={maintenance || ''}
                onCreate={() => setAlertInfo({ message: 'Comentário Criado!', open: true, severity: 'success' })}
                onError={() => setAlertInfo({ message: 'Erro ao criar!', open: true, severity: 'error' })}
                invalid={() => setAlertInfo({ message: 'Preencha o campo de comentário', open: true, severity: 'warning' })}
            />
            {alertInfo.open &&
                <CustomAlert
                    key={alertInfo.message}
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
}